
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3, Quaternion, Vector3 } from '@babylonjs/core/Maths/math';
import { CreateBox, CreateCylinder, CreatePlane, CreateSphere } from '@babylonjs/core/Meshes/Builders';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Scene } from '@babylonjs/core/scene';

import { eUvMode } from '@kb3d/models/classes/enums';
import { UvMapFeature } from '@kb3d/models/classes/features/uv-map-feature';
import { Token } from '@kb3d/models/classes/token';
import { ITrackedClass } from '@kb3d/models/interfaces';
import { rotationToVector } from '@kb3d/view/helpers';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';

import { FeatureRenderer } from './feature';
import { UvMapGeometryProcessor } from './geometry-processor/uvmap-geometry';

export class UvMapRenderer extends FeatureRenderer<UvMapFeature> {
    modelToken = Token.UvMapFeature;
    babylonUvPreviewsMap = new Map<string, Mesh>();
    previewLayer: UtilityLayerRenderer = this.viewer.utilityLayer;
    processor = new UvMapGeometryProcessor(this.scene);

    protected updatePreviewMesh(feature: UvMapFeature) {
        let visualizationMesh = this.babylonUvPreviewsMap.get(feature.id);
        const scene = this.previewLayer.utilityLayerScene;

        if (!visualizationMesh) {
            visualizationMesh = createUvMapVisualization(feature, scene, Color3.Yellow());
            visualizationMesh.isPickable = false;
            this.babylonUvPreviewsMap.set(feature.id, visualizationMesh);
        } else {
            this.deletePreview3d(feature);
            visualizationMesh = createUvMapVisualization(feature, scene, Color3.Yellow());
            visualizationMesh.isPickable = false;
            this.babylonUvPreviewsMap.set(feature.id, visualizationMesh);
        }

        let meshPosition = visualizationMesh.getAbsolutePosition();
        if (feature._parent) {
            const featureParent = this.viewer.getRendererNode(feature._parent);
            meshPosition = featureParent.getAbsolutePosition();
        }
        meshPosition.add(feature.center.toVec3());
        meshPosition.addInPlace(feature.center.toVec3());
        visualizationMesh.position.set(meshPosition.x, meshPosition.y, meshPosition.z);
        //visualizationMesh.project;

        const newNormal = new Vector3();

        newNormal.set(feature.projectionAxis.x, feature.projectionAxis.y, feature.projectionAxis.z);

        visualizationMesh.rotationQuaternion = Quaternion.RotationAxis(newNormal, (feature.angle / 180) * Math.PI);
        visualizationMesh.rotationQuaternion = visualizationMesh.rotationQuaternion.multiply(
            rotationToVector(newNormal)
        );
        visualizationMesh.scaling.set(feature.scale.x, feature.scale.y, feature.scale.z);
    }

    update3dInternal(feature: ITrackedClass<UvMapFeature>) {}

    postUpdateGeometry(feature: ITrackedClass<UvMapFeature>, _vertexData: SubmeshVertexData): void {
        const visualizationMesh = this.babylonUvPreviewsMap.get(feature.id);

        visualizationMesh?.setEnabled(feature._previewMaterialVisible);
        if (feature._previewMaterialVisible) {
            this.updatePreviewMesh(feature);
        }
    }

    delete3d(feature: UvMapFeature) {
        this.deletePreview3d(feature);
        super.delete3d(feature as ITrackedClass<UvMapFeature>);
    }

    deletePreview3d(feature: UvMapFeature) {
        const previewMesh = this.babylonUvPreviewsMap.get(feature.id);
        if (previewMesh) {
            previewMesh.dispose();
        }
        this.babylonUvPreviewsMap.delete(feature.id);
    }
}

export function createUvMapVisualization(uvMap: UvMapFeature, scene: Scene, color?: Color3) {
    let uvMapVisualization: Mesh;

    if (!color) {
        color = Color3.Yellow();
    }
    const coloredMaterial = new StandardMaterial('', scene);
    coloredMaterial.wireframe = true;
    coloredMaterial.emissiveColor = color;

    if (uvMap.mode === eUvMode.spherical) {
        uvMapVisualization = CreateSphere('UvMapSphere', { segments: 6 }, scene);
    } else if (uvMap.mode === eUvMode.cylindrical) {
        uvMapVisualization = CreateCylinder('UvMapCylinder', { tessellation: 10 }, scene);
    } else if (uvMap.mode === eUvMode.box) {
        uvMapVisualization = CreateBox('UvBox', {}, scene);
    } else if (uvMap.mode === eUvMode.planar) {
        uvMapVisualization = CreatePlane('mirrorPlane', {}, scene);
        uvMapVisualization.rotation = new Vector3(Math.PI / 2, 0, 0);
        uvMapVisualization.bakeCurrentTransformIntoVertices();
    } else {
        uvMapVisualization = CreatePlane('mirrorPlane', {}, scene);
        uvMapVisualization.bakeCurrentTransformIntoVertices();
        uvMapVisualization.setEnabled(false);
    }
    uvMapVisualization.material = coloredMaterial;

    return uvMapVisualization;
}
